import { Box } from "@mui/material";
import PlanningAppealsService from "@publicPortal/services/PlanningAppealsService";
import { Serializer } from "@shared/mirage/helpers/Serializer";
import { memo, useCallback, useEffect } from "react";
import { PlanningAppealsSearchState } from "./PlanningAppealsSearchState";
import PlanningAppealsSimpleSearch from "./PlanningAppealsSimpleSearch";
import { PlanningAppealsFilter } from "@shared/publicportal/contracts/PlanningAppealsFilter";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import { usePlanningAppealsSearchStore } from "@publicPortal/store/store";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import SearchActions from "../SearchActions";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SearchType } from "@shared/publicportal/contracts/Search";
import PlanningAppealsAdvancedSearch from "@publicPortal/components/advancedSearch/PlanningAppealsAdvancedSearch";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { PublicPortalMapSearch } from "@publicPortal/components/mapSearch/PublicPortalMapSearch";

const service = new PlanningAppealsService();
const pp = new PublicPortalRoutingService();

export const PlanningAppealsSearch = memo(() => {
    const navigate = useStableNavigate();
    const state = usePlanningAppealsSearchStore((s) => s.planningAppealsSearch);
    const updatePlanningAppealsSearch = usePlanningAppealsSearchStore((s) => s.updatePlanningAppealsSearch);
    const updatePlanningAppealsFilter = usePlanningAppealsSearchStore((s) => s.updatePlanningAppealsFilter);
    const resetPlanningAppealsFilter = usePlanningAppealsSearchStore((s) => s.resetPlanningAppealsFilter);

    const setStateModifier = useCallback(
        (modifier: Partial<PlanningAppealsFilter>) => {
            updatePlanningAppealsFilter(modifier);
        },
        [updatePlanningAppealsFilter]
    );
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            updatePlanningAppealsSearch({ searchType: newSearchType });
        },
        [updatePlanningAppealsSearch]
    );
    const prepareFilter = useCallback(() => {
        switch (state.searchType) {
            case "Simple": {
                return {
                    searchType: state.searchType,
                    appealRef: state.filter.appealRef,
                    applicationRef: state.filter.applicationRef,
                    planningInspectorateRef: state.filter.planningInspectorateRef,
                };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }, [state.filter, state.searchType]);
    const onCancel = useCallback(() => {
        resetPlanningAppealsFilter();
    }, [resetPlanningAppealsFilter]);
    const onSubmit = useCallback(() => {
        const filter = prepareFilter();
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
        EntityTableStateStorage.clear("PlanningAppeals");
        navigate(`${pp.SearchRoute("PlanningAppeals", searchParams)}`);
    }, [navigate, prepareFilter]);

    useEffect(() => {
        service.getFilteringLists().then((filter) => {
            const lists = { appealTypes: filter.AppealTypes, appealDecisionTypes: filter.AppealDecisionTypes, wards: filter.Wards, parishes: filter.Parishes };
            updatePlanningAppealsSearch(lists);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            {getForm()}
            <SearchActions onCancelHandler={onCancel} onSubmitHandler={onSubmit} />
        </Box>
    );

    function getForm() {
        switch (state.searchType) {
            case "Simple": {
                return <PlanningAppealsSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <PlanningAppealsAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return <PublicPortalMapSearch setStateModifier={setStateModifier} geometry={state.filter.geometry} />;
            }
            default:
                return null;
        }
    }
});
